import { forwardRef, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { Launch, type CarbonIconType } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, type LinkProps as RouterLinkProps } from 'react-router';
import { cx } from '@/shared/lib/cx';
import { buttonClassName, type ButtonSize, type ButtonVariant } from './Button';
import s from './Link.module.css';

type LinkVariant = 'inline' | 'standalone';

interface CommonLinkProps {
  variant?: LinkVariant;
  icon?: CarbonIconType;
  children: ReactNode;
  className?: string;
}

export type LinkProps = CommonLinkProps &
  (
    | ({ to: RouterLinkProps['to']; href?: never } & Omit<RouterLinkProps, 'to' | 'className' | 'children'>)
    | ({ href: string; to?: never; external?: boolean } & Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'className' | 'children'>)
  );

/**
 * Ссылка: внутренняя — через маршрутизатор (`to`), внешняя — `href` (открывается в новой
 * вкладке с пометкой для экранных дикторов и безопасным rel).
 */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  const { t } = useTranslation('ui');
  const { variant = 'inline', icon: Icon, children, className } = props;
  const classes = cx(s.link, variant === 'standalone' && s.standalone, className);
  if ('href' in props && props.href !== undefined) {
    const { href, external = true, variant: _variant, icon: _icon, children: _children, className: _className, ...anchorProps } = props;
    return (
      <a
        ref={ref}
        href={href}
        className={classes}
        {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...anchorProps}
      >
        {Icon ? <Icon size={16} aria-hidden="true" /> : null}
        {children}
        {external ? (
          <>
            <Launch size={16} aria-hidden="true" className={s.externalIcon} />
            <span className="visually-hidden"> ({t('externalLink')})</span>
          </>
        ) : null}
      </a>
    );
  }
  const { to, variant: _variant, icon: _icon, children: _children, className: _className, ...routerProps } = props as Extract<LinkProps, { to: RouterLinkProps['to'] }>;
  return (
    <RouterLink ref={ref} to={to} className={classes} {...routerProps}>
      {Icon ? <Icon size={16} aria-hidden="true" /> : null}
      {children}
    </RouterLink>
  );
});

export interface ButtonLinkProps extends Omit<RouterLinkProps, 'className'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: CarbonIconType;
  className?: string;
}

/** Переход, оформленный как кнопка (например, «Выбрать тариф» в состоянии лимита). */
export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(function ButtonLink(
  { variant = 'primary', size = 'md', icon: Icon, className, children, ...rest },
  ref,
) {
  return (
    <RouterLink ref={ref} className={buttonClassName({ variant, size, className })} {...rest}>
      {Icon ? <Icon size={16} aria-hidden="true" /> : null}
      <span>{children as ReactNode}</span>
    </RouterLink>
  );
});
